import React, { useEffect, useState } from 'react';

import { Card } from '@/cms/Card';
import { CMSSection } from '@/cms/CMSSection';
import type { ProductCardProps } from '@/cms/ProductCard';
import { ProductCard } from '@/cms/ProductCard';
import {
  MediaWrapper,
  type MediaWrapperProps,
} from '@/components/Cms/MediaWrapper';
import { Container } from '@/components/Container';
import type {
  ContentfulCardFragment,
  ContentfulContentGroupFragment,
} from '@/graphql/contentful/api';
import { useIsMdDown } from '@/hooks/useMedia';
import type { CmsExtraDataRecord } from '@/integrations/contentful';
import { generateSectionKey } from '@/integrations/contentful';
import type {
  ContentfulData,
  ContentType,
} from '@/integrations/contentful/types';
import { generateDataTestId } from '@/lib/tests';
import type { WithHTMLProp } from '@/lib/types';

import type { CardProps } from '../Card';

import * as S from './FourColumnScroll.styles';

export type FourColumnScrollProps = ContentfulContentGroupFragment & {
  extraDataRecord?: CmsExtraDataRecord;
} & WithHTMLProp<'className'>;

export interface WallItemProps extends ContentfulCardFragment {
  preload?: boolean;
}
export const FourColumnScroll = ({
  className,
  extraDataRecord,
  itemsCollection,
}: FourColumnScrollProps) => {
  const isMobileView = useIsMdDown();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    if (isMobileView === true && isMobileView !== isMobile) {
      setIsMobile(true);
    } else if (isMobileView === false && isMobileView !== isMobile) {
      setIsMobile(false);
    }
  }, [isMobileView]);
  return (
    <CMSSection
      className={className}
      {...generateDataTestId('contentCollection', 'section')}
      data-test-id='FourColumnScrollCMSSection'
    >
      <Container>
        {isMobile ? (
          <S.FlexWrapper data-testid='FourColumnScrollFlexWrapper'>
            {itemsCollection?.items.filter(Boolean).map((item, i, array) => {
              const props = (item ? { ...item } : null) as ItemData;
              if (props) {
                props.preload = true;
              }
              const renderedItem = renderCollectionFlexItem(
                props,
                i,
                array.length,
                extraDataRecord,
              );
              return renderedItem ? (
                renderCollectionFlexItem(
                  item as ItemData,
                  i,
                  array.length,
                  extraDataRecord,
                )
              ) : (
                <></>
              );
            })}
          </S.FlexWrapper>
        ) : (
          <S.GridWrapper data-testid='FourColumnScrollGridWrapper'>
            {itemsCollection?.items.filter(Boolean).map((item, i, array) => {
              const props = (item ? { ...item } : null) as ItemData;
              if (props) {
                props.preload = true;
              }
              const renderedItem = renderCollectionGridItem(
                props,
                i,
                array.length,
                extraDataRecord,
              );
              return renderedItem ? (
                renderCollectionGridItem(
                  item as ItemData,
                  i,
                  array.length,
                  extraDataRecord,
                )
              ) : (
                <></>
              );
            })}
          </S.GridWrapper>
        )}
      </Container>
    </CMSSection>
  );
};

type ItemData = CardProps | MediaWrapperProps | ProductCardProps | null;

export const renderCollectionFlexItem = (
  data: ItemData,
  i: number,
  arrayLength: number,
  extraDataRecord?: CmsExtraDataRecord,
) => {
  let props = null;
  const typename = data?.__typename as ContentType;
  const sysId = data?.sys?.id;
  const extraData = extraDataRecord ? extraDataRecord[sysId ?? ''] : undefined;
  switch (typename) {
    case 'Card':
      props = data as WallItemProps;
      return (
        <S.FlexItem
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`FourColumnScrollCard-${i}`}
        >
          <Card
            {...props}
            data-test-id='FourColumnScrollCard'
            extraData={extraData?.extraData}
            mediaWrapperType='FourColumnScroll'
          />
        </S.FlexItem>
      );
    case 'ImageWithFocalPoint':
      props = data as MediaWrapperProps;
      return (
        <S.GridItem
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`FourColumnScrollImage-${i}`}
        >
          <MediaWrapper
            {...props}
            data-test-id='FourColumnScrollImage'
            mediaWrapperType='FourColumnScroll'
          />
        </S.GridItem>
      );
    case 'ProductCard':
      props = data as ProductCardProps;
      return (
        <S.FlexItem
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`FourColumnScrollProductCard-${i}`}
        >
          <ProductCard
            autoLoadCTA={true}
            {...props}
            data-test-id='FourColumnScrollProductCard'
          />
        </S.FlexItem>
      );
    default:
      return null;
  }
};
export const renderCollectionGridItem = (
  data: ItemData,
  i: number,
  arrayLength: number,
  extraDataRecord?: CmsExtraDataRecord,
) => {
  let props = null;
  const typename = data?.__typename as ContentType;
  const sysId = data?.sys?.id;
  const extraData = extraDataRecord ? extraDataRecord[sysId ?? ''] : undefined;
  switch (typename) {
    case 'Card':
      props = data as WallItemProps;
      return (
        <S.GridItem
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`FourColumnScrollCard-${i}`}
        >
          <Card
            {...props}
            data-test-id='FourColumnScrollCard'
            extraData={extraData?.extraData}
            mediaWrapperType='FourColumnScroll'
          />
        </S.GridItem>
      );
    case 'ImageWithFocalPoint':
      props = data as MediaWrapperProps;
      return (
        <S.GridItem
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`FourColumnScrollImage-${i}`}
        >
          <MediaWrapper
            {...props}
            data-test-id='FourColumnScrollImage'
            mediaWrapperType='FourColumnScroll'
          />
        </S.GridItem>
      );
    case 'ProductCard':
      props = data as ProductCardProps;
      return (
        <S.GridItem
          key={generateSectionKey(data! as ContentfulData, i)}
          data-test-id={`FourColumnScrollProductCard-${i}`}
        >
          <ProductCard
            autoLoadCTA={true}
            {...props}
            data-test-id='FourColumnScrollProductCard'
          />
        </S.GridItem>
      );
    default:
      return null;
  }
};

FourColumnScroll.displayName = 'FourColumnScroll';
